import React, {ReactNode} from 'react';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {StartDocumentButton} from 'app/components/general/StartDocumentPopover';
import {
  getDescriptionContent,
  type VersionDiffType,
} from 'app/components/VersionDiffer';
import {DocumentFoldersList} from 'app/folders/components/DocumentFoldersList';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {ManagersList} from 'app/procedures/components/ViewProcedureMetaData/ManagersList';
import {ModifiedCreatedDataStamp} from 'app/procedures/components/ViewProcedureMetaData/ModifiedCreatedDataStamp';
import {useAccountSettingsState} from 'app/settings/api';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getProcessName} from 'app/utils/fallbacks';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import type {
  NestedPolicyProcessSerializedType,
  ProcessHashidSerializedType,
  TaggedItemSerializedType,
  TaskInstanceProcessAssignedVersionContent,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface CommonProps {
  advancedModeEnabled?: boolean;
  metaContentType?: 'proceduregroup' | 'taskinstance';
  warningComponent?: ReactNode;
  content: TaskInstanceProcessAssignedVersionContent | VersionDiffType;
  tags: Array<TaggedItemSerializedType>;
  policies: Array<NestedPolicyProcessSerializedType>;
  extraContent?: ReactNode;
  editableEmptyDataMessage?: string;
  editMode?: boolean;
}

interface WithTaskProps extends CommonProps {
  task: TaskInstanceSerializedType;
}

interface WithProcessProps extends CommonProps {
  process: ProcessHashidSerializedType;
}

type Props =
  | WithTaskProps
  | WithProcessProps
  | (WithProcessProps & WithTaskProps);

export const ViewProcessMetadata = ({
  advancedModeEnabled,
  warningComponent,
  content,
  policies,
  tags,
  extraContent,
  editableEmptyDataMessage,
  metaContentType = 'proceduregroup',
  editMode,
  ...props
}: Props): JSX.Element => {
  const hasNoTagsOrDescription =
    tags.length === 0 && getDescriptionContent(content.description) === '';

  const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;

  return (
    <VideoManagerProvider>
      <DocHeaderStructure
        warningContent={warningComponent}
        advancedContent={
          <>
            {advancedModeEnabled === true && 'process' in props ? (
              <div className="row">
                <div className="col">
                  <ModifiedCreatedDataStamp document={props.process} />
                  {props.process.managers != null ? (
                    <ManagersList document={props.process} />
                  ) : null}
                </div>
              </div>
            ) : null}
            <MetaContentTypeName
              contentType={metaContentType}
              statusContent={
                <DocumentMetadataStatusButtons
                  canEdit={!('task' in props) && props.process.can_edit}
                  contentType="proceduregroup"
                  context={'task' in props ? 'task' : 'view'}
                  document={
                    'process' in props
                      ? props.process
                      : props.task.template.assigned_object
                  }
                />
              }
            />
          </>
        }
        titleContent={
          <div
            className={
              isRightToLeftWrapper(
                'task' in props && props.task.description != null
                  ? props.task.description
                  : getDescriptionContent(content.description)
              )
                ? 'right-to-left-text'
                : ''
            }
          >
            <h1>
              {'task' in props && props.task.description != null
                ? props.task.description
                : typeof content.name === 'string'
                ? getProcessName(content, true)
                : content.name}
              {editMode ? <ClickToEditButton /> : null}
            </h1>
            {'task' in props && props.task.description != null ? (
              <h4>
                {typeof content.name === 'string'
                  ? getProcessName(content, true)
                  : content.name}
              </h4>
            ) : null}
            {'process' in props && !('task' in props) ? (
              <span
                className={`d-print-none ${
                  isRightToLeftWrapper(content.name)
                    ? 'float-left'
                    : 'float-right'
                } mr-1 ml-1`}
              >
                <StartDocumentButton
                  objectId={props.process.id}
                  contentType="proceduregroup"
                />
              </span>
            ) : null}
          </div>
        }
        tagsContent={
          <>
            {hasNoTagsOrDescription && editableEmptyDataMessage != null ? (
              <div className="form-group">
                <span className="small text-secondary font-weight-light">
                  {editableEmptyDataMessage}
                </span>
              </div>
            ) : (
              <DocumentTagsList tags={tags} />
            )}
            {'process' in props ? (
              <DocumentFoldersList connections={props.process.connections} />
            ) : null}
          </>
        }
        policyContent={<DocumentPolicyList policies={policies} />}
        descriptionContent={
          content.description ? (
            <UserContentContainer
              mediaURL={MEDIA_URL}
              mediaURLRead={MEDIA_URL_READ}
              content={getDescriptionContent(content.description)}
            />
          ) : null
        }
        extraContent={extraContent}
      />
    </VideoManagerProvider>
  );
};
